import React, { useMemo } from 'react';
import { SingleValue } from 'react-select';
import classnames from 'classnames/bind';

import { AdminSingleSectionMeta } from '@INTERFACES/admin-api/sections';
import BasicSelect from '@COMPONENTS/COMMON/inputs/select/inputs/BasicSelect';

import styles from './VariantFilters.module.scss';

const cx: CX = classnames.bind(styles);

export interface FilterOption {
    value: number;
    label: string;
}

export interface VariantFiltersData {
    brand: FilterOption | null;
    region: FilterOption | null;
    area: FilterOption | null;
    country: FilterOption | null;
    propertyType: FilterOption | null;
}

interface Props {
    sectionMeta: AdminSingleSectionMeta;
    filters: VariantFiltersData;
    onFiltersChange: (filters: VariantFiltersData) => void;
    onClearFilters: () => void;
}

function VariantFilters(props: Props) {
    const {
        sectionMeta,
        filters,
        onFiltersChange,
        onClearFilters,
    } = props;

    const filterOptions = useMemo(() => {
        const metaOptions = sectionMeta.options || {
            brands: [],
            regions: [],
            areas: [],
            countries: [],
            propertyTypes: [],
        };

        return {
            brands: metaOptions.brands.map((item) => ({
                value: item.id,
                label: item.name,
            })),
            regions: metaOptions.regions.map((item) => ({
                value: item.id,
                label: item.name,
            })),
            areas: metaOptions.areas.map((item) => ({
                value: item.id,
                label: item.name,
            })),
            countries: metaOptions.countries.map((item) => ({
                value: item.id,
                label: item.name,
            })),
            propertyTypes: metaOptions.propertyTypes.map((item) => ({
                value: item.id,
                label: item.name,
            })),
        };
    }, [sectionMeta]);

    const hasActiveFilters = useMemo(() => Object.values(filters).some((filter) => filter !== null), [filters]);

    const handleFilterChange = (filterKey: keyof VariantFiltersData, value: FilterOption | null) => {
        onFiltersChange({
            ...filters,
            [filterKey]: value,
        });
    };

    return (
        <div className={cx('variant-filters')}>
            <div className={cx('filters-header')}>
                <h3 className={cx('filters-title')}>Filters</h3>
                {hasActiveFilters && (
                    <button
                        type="button"
                        className={cx('clear-filters-btn')}
                        onClick={onClearFilters}
                    >
                        Clear All
                    </button>
                )}
            </div>

            <div className={cx('filters-grid')}>
                <div className={cx('filter-item')}>
                    <BasicSelect
                        title="Brand"
                        placeholder="All Brands"
                        options={filterOptions.brands}
                        value={filters.brand}
                        onChange={(val: SingleValue<FilterOption>) => handleFilterChange('brand', val)}
                        isClearable
                    />
                </div>

                <div className={cx('filter-item')}>
                    <BasicSelect
                        title="Region"
                        placeholder="All Regions"
                        options={filterOptions.regions}
                        value={filters.region}
                        onChange={(val: SingleValue<FilterOption>) => handleFilterChange('region', val)}
                        isClearable
                    />
                </div>

                <div className={cx('filter-item')}>
                    <BasicSelect
                        title="Area"
                        placeholder="All Areas"
                        options={filterOptions.areas}
                        value={filters.area}
                        onChange={(val: SingleValue<FilterOption>) => handleFilterChange('area', val)}
                        isClearable
                    />
                </div>

                <div className={cx('filter-item')}>
                    <BasicSelect
                        title="Country"
                        placeholder="All Countries"
                        options={filterOptions.countries}
                        value={filters.country}
                        onChange={(val: SingleValue<FilterOption>) => handleFilterChange('country', val)}
                        isClearable
                    />
                </div>

                <div className={cx('filter-item')}>
                    <BasicSelect
                        title="Property Type"
                        placeholder="All Types"
                        options={filterOptions.propertyTypes}
                        value={filters.propertyType}
                        onChange={(val: SingleValue<FilterOption>) => handleFilterChange('propertyType', val)}
                        isClearable
                    />
                </div>
            </div>
        </div>
    );
}

export default VariantFilters; 